import { MenuItem, TableCell, TableRow } from "@mui/material";
import { useState } from "react";
import { useMutation } from "react-query";
import BootstrapModal from "react-bootstrap/Modal";
import moment from "moment";
import useLang from "Shared/hooks/useLanguage";
import { toast } from "react-toastify";
import {
  StyledDropButtonThreeDots,
  StyledDropMenuMoreAction,
} from "Utils/Style/Dropdownstyle";
import SearchDatePicker from "Shared/Common/DatePicker/SearchDatePicker";
import {
  BatchReagentsSave,
  BatchReagentsToggleStatus,
} from "Services/BatchReagents/BatchReagents";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";

function ActiveReagentsRow({
  row,
  index,
  // serialNumber,
  setLocalRows,
  refetch,
  setIsBtnDisabled,
}: any) {
  const { t } = useLang();
  const [anchorEl, setAnchorEl] = useState<any>({ dropdown1: null });
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const isNewRow = row.batchReagentsId === 0;

  const handleClick = (event: any, name: string) =>
    setAnchorEl({ ...anchorEl, [name]: event.currentTarget });

  const handleClose = (name: string) =>
    setAnchorEl({ ...anchorEl, [name]: null });

  const handleChange = (field: string, value: string) => {
    if (isNewRow) {
      setLocalRows((curr: any[]) =>
        curr.map((x: any, i: number) =>
          i === index ? { ...x, [field]: value } : x
        )
      );
    } else {
      setLocalRows((curr: any[]) =>
        curr.map((x: any) =>
          x.batchReagentsId === row.batchReagentsId
            ? { ...x, [field]: value }
            : x
        )
      );
    }
  };

  const getValues = (r: any) => {
    const toDisplay = (d: string | null) =>
      d ? moment(d).format("MM/DD/YYYY") : "";
    setLocalRows((curr: any[]) => [
      {
        ...r,
        rowStatus: true,
        startedDate: toDisplay(r.startedDate),
        endDate: toDisplay(r.endDate),
        expirationDate: toDisplay(r.expirationDate),
      },
      ...curr.filter((x: any) => x.batchReagentsId !== r.batchReagentsId),
    ]);
  };

  const removeFromLocalRows = () => {
    if (isNewRow) {
      setLocalRows((curr: any[]) =>
        curr.filter((_: any, i: number) => i !== index)
      );
    } else {
      setLocalRows((curr: any[]) =>
        curr.filter((x: any) => x.batchReagentsId !== row.batchReagentsId)
      );
    }
  };

  const handleRowCancel = () => {
    handleClose("dropdown1");
    removeFromLocalRows();
    if (isNewRow) setIsBtnDisabled(false);
  };

  const toISO = (d: string | null | undefined) =>
    d ? moment.utc(d, "MM/DD/YYYY").startOf("day").toISOString() : null;

  const formatDate = (d: string | null | undefined) =>
    d ? moment(d).format("MM/DD/YYYY") : "";

  const saveMutation = useMutation(
    (payload: any) => BatchReagentsSave(payload),
    {
      onSuccess: (res: any) => {
        if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
          toast.success(res?.data?.data?.message ?? t("Saved successfully."));
          setIsBtnDisabled(false);
          handleClose("dropdown1");
          removeFromLocalRows();
          refetch();
        } else {
          toast.error(res?.data?.data?.message ?? t("Something went wrong."));
        }
      },
      onError: () => { toast.error(t("An unexpected error occurred.")); },
    }
  );

  const deactivateMutation = useMutation(
    (id: number) => BatchReagentsToggleStatus(id),
    {
      onSuccess: (res: any) => {
        if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
          toast.success(res?.data?.data?.message ?? t("Status updated."));
          handleClose("dropdown1");
          setShowDeactivateModal(false);
          refetch();
        } else {
          toast.error(res?.data?.data?.message ?? t("Something went wrong."));
        }
      },
      onError: () => { toast.error(t("An unexpected error occurred.")); },
    }
  );

  const handleDeactivate = () => {
    if (!row.batchReagentsId) return toast.info(t("No Row Selected"));
    deactivateMutation.mutate(row.batchReagentsId);
  };

  const openDeactivateModal = () => {
    handleClose("dropdown1");
    setShowDeactivateModal(true);
  };

  const handleSave = () => {
    if (!row.reagentsName?.trim()) {
      toast.error(t("Reagents name cannot be empty."));
      return;
    }
    saveMutation.mutate({
      batchReagentsId: row.batchReagentsId ?? 0,
      reagentsName: row.reagentsName,
      previousLotNumber: row.previousLotNumber ?? "",
      reagentLotNumber: row.reagentLotNumber ?? "",
      startedDate: toISO(row.startedDate),
      endDate: toISO(row.endDate),
      expirationDate: toISO(row.expirationDate),
    });
  };

  return (
    <>
      <TableRow className="h-30px">
        <TableCell>
          {row.rowStatus ? (
            <div className="d-flex justify-content-center gap-2">
              <button
                disabled={saveMutation.isLoading}
                id={`ActiveReagentSave_${row.batchReagentsId}`}
                className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                onClick={handleSave}
              >
                <i className="bi bi-check2" />
              </button>
              <button
                id={`ActiveReagentCancel_${row.batchReagentsId}`}
                className="btn btn-icon btn-sm fw-bold btn-table-cancel btn-icon-light h-32px w-32px fas-icon-20px"
                onClick={handleRowCancel}
              >
                <i className="bi bi-x" />
              </button>
            </div>
          ) : (
            <div className="d-flex justify-content-center">
              <StyledDropButtonThreeDots
                id={`ActiveReagent3Dots_${row.batchReagentsId}`}
                aria-haspopup="true"
                onClick={(e: any) => handleClick(e, "dropdown1")}
                className="btn btn-light-info btn-sm btn-icon moreactions min-w-auto rounded-4"
              >
                <i className="bi bi-three-dots-vertical p-0 icon" />
              </StyledDropButtonThreeDots>
              <StyledDropMenuMoreAction
                anchorEl={anchorEl.dropdown1}
                open={Boolean(anchorEl.dropdown1)}
                onClose={() => handleClose("dropdown1")}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <PermissionComponent
                  moduleName="Oncology LIS"
                  pageName="Reagent"
                  permissionIdentifier="Edit"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <MenuItem className="p-0">
                      <a
                        id={`ActiveReagentEdit_${row.batchReagentsId}`}
                        className="w-150px p-0 text-dark"
                        onClick={() => { getValues(row); handleClose("dropdown1"); }}
                      >
                        <div className="menu-item px-3">
                          <i className="fa fa-edit text-primary mr-2 w-20px" />
                          {getPermissionDisplayName(
                            pageName,
                            moduleName,
                            permissionIdentifier,
                            "Edit"
                          )}
                        </div>
                      </a>
                    </MenuItem>
                  )}
                </PermissionComponent>
                <PermissionComponent
                  moduleName="Oncology LIS"
                  pageName="Reagent"
                  permissionIdentifier="Inactive"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <MenuItem className="p-0">
                      <a
                        id={`ActiveReagentDeactivate_${row.batchReagentsId}`}
                        className="w-150px p-0 text-dark"
                        onClick={openDeactivateModal}
                      >
                        <div className="menu-item px-3">
                          <i className="fa fa-ban text-danger mr-2 w-20px" />
                          {getPermissionDisplayName(
                            pageName,
                            moduleName,
                            permissionIdentifier,
                            "Inactive"
                          )}
                        </div>
                      </a>
                    </MenuItem>
                  )}
                </PermissionComponent>
              </StyledDropMenuMoreAction>
            </div>
          )}
        </TableCell>

        {/* Sr.No */}
        {/* <TableCell>
          <div style={{ width: "max-content" }}>{serialNumber}</div>
        </TableCell> */}

        {/* Reagents Name */}
        <TableCell sx={{ width: "max-content" }}>
          {row.rowStatus ? (
            <input
              type="text"
              name="reagentsName"
              placeholder={t("Reagents Name")}
              className="form-control bg-white mb-lg-0 min-w-150px w-100 rounded h-30px"
              value={row.reagentsName ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.reagentsName}</div>
          )}
        </TableCell>

        {/* Previous Lot No */}
        <TableCell sx={{ width: "max-content" }}>
          {row.rowStatus ? (
            <input
              type="text"
              name="previousLotNumber"
              placeholder={t("Previous Lot No")}
              className="form-control bg-white mb-lg-0 min-w-130px w-100 rounded h-30px"
              value={row.previousLotNumber ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.previousLotNumber}</div>
          )}
        </TableCell>

        {/* Reagents Lot # */}
        <TableCell sx={{ width: "max-content" }}>
          {row.rowStatus ? (
            <input
              type="text"
              name="reagentLotNumber"
              placeholder={t("Reagents Lot #")}
              className="form-control bg-white mb-lg-0 min-w-120px w-100 rounded h-30px"
              value={row.reagentLotNumber ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.reagentLotNumber}</div>
          )}
        </TableCell>

        {/* Started Date */}
        <TableCell sx={{ minWidth: "160px" }}>
          {row.rowStatus ? (
            <SearchDatePicker
              name="startedDate"
              value={row.startedDate ?? ""}
              onChange={(e: any) => handleChange("startedDate", e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{formatDate(row.startedDate)}</div>
          )}
        </TableCell>

        {/* Ended Date */}
        <TableCell sx={{ minWidth: "160px" }}>
          {row.rowStatus ? (
            <SearchDatePicker
              name="endDate"
              value={row.endDate ?? ""}
              onChange={(e: any) => handleChange("endDate", e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{formatDate(row.endDate)}</div>
          )}
        </TableCell>

        {/* Expiration Date */}
        <TableCell sx={{ minWidth: "160px" }}>
          {row.rowStatus ? (
            <SearchDatePicker
              name="expirationDate"
              value={row.expirationDate ?? ""}
              onChange={(e: any) =>
                handleChange("expirationDate", e.target.value)
              }
            />
          ) : (
            <div style={{ width: "max-content" }}>{formatDate(row.expirationDate)}</div>
          )}
        </TableCell>

        {/* Edit By */}
        <TableCell sx={{ width: "max-content" }}>
          <div style={{ width: "max-content" }}>{row.updatedBy ?? ""}</div>
        </TableCell>
      </TableRow>

      {/* Deactivate Confirmation Modal */}
      <BootstrapModal
        show={showDeactivateModal}
        onHide={() => setShowDeactivateModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <BootstrapModal.Header closeButton className="bg-light-primary m-0 p-5">
          <h4>{t("Inactive Reagent")}</h4>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          {t("Are you sure you want to deactivate this reagent?")}
        </BootstrapModal.Body>
        <BootstrapModal.Footer className="p-0">
          <button
            id={`ActiveReagentModalDeactivateCancel_${row.batchReagentsId}`}
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowDeactivateModal(false)}
          >
            {t("Cancel")}
          </button>
          <button
            id={`ActiveReagentModalDeactivateConfirm_${row.batchReagentsId}`}
            type="button"
            className="btn btn-danger m-2"
            onClick={handleDeactivate}
          >
            {t("Inactive")}
          </button>
        </BootstrapModal.Footer>
      </BootstrapModal>
    </>
  );
}

export default ActiveReagentsRow;
